import { useEffect, useRef, useState } from 'react';
import { Button, Group, Loader, Table } from '@mantine/core';
import { useSelector } from 'react-redux';
import {
  Declaration,
  fetchEquipmentDeclarations,
  fetchEquipmentReasons,
  Reason
} from '../../../libraries/reason-declaration';
import PaginationComponent from '../../components/pagination';
import { LoginState } from '../../client-redux/login/reducer';
import { AppState } from '../../client-redux/reducers';
import FilterBar, { FilterBarFieldsData } from '../../components/filterbar';
import Header from '../../components/util/header';
import { getLanguageName } from '../../languages/languageUtils';
import { Language } from '../../languages/languageHandler';
import EquipmentReasonDeclaration from './equipment-reason-declaration';
import DeleteEquipmentReasonDeclaration from './remove-equipment-reasons-declaration';
import { useFitTableRows } from '../../components/util/fit-table-rows-hook';
import { isZarphSuccessResponse, useStatusOperDialog } from '../../context/status-oper-dialog';
import { IconPlus } from '@tabler/icons-react';
import './styles.scss';

const ReasonsDeclarations = () => {
  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  const user = useSelector<AppState, LoginState>(state => state.login);
  const controller = useRef(new AbortController()).current;

  const [reasons, setReasons] = useState<Reason[]>([]);
  const [declarations, setDeclarations] = useState<Declaration[]>([]);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [loadingReasons, setLoadingReasons] = useState(false);
  const [loadingDeclarations, setLoadingDeclarations] = useState(false);

  const {
    limit = 10,
    setLimit,
    tableRef
  } = useFitTableRows({
    bufferRows: 0,
    minHeightForDynamic: 400
  });
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [offset, setOffset] = useState<number>(0);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [modalData, setModalData] = useState({
    code: 0,
    language: 0,
    text: '',
    type: ''
  });
  const [filterBarData, setFilterBarData] = useState<FilterBarFieldsData>();

  const { handleApiError } = useStatusOperDialog();

  useEffect(() => {
    if (filterBarData && limit) {
      loadLists();
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterBarData, limit, offset]);

  const handleFilterBarOnDataChange = (data: FilterBarFieldsData) => {
    setFilterBarData(data);
    changePage(1);
  };

  const loadLists = async (type?: 'reason' | 'declaration') => {
    if (!filterBarData || !limit) return;

    setTotalRows(0);

    const entid = filterBarData.entity || user.user?.entid || 0;

    try {
      if (!type || type === 'reason') {
        setLoadingReasons(true);
        const reasonsResponse = await fetchEquipmentReasons(controller, entid);

        if (isZarphSuccessResponse(reasonsResponse)) {
          setReasons(reasonsResponse.data?.reasons || []);
        } else {
          handleApiError(reasonsResponse, lang.ALERT_ERROR_FETCHING_DATA);
        }
        setLoadingReasons(false);
      }

      if (!type || type === 'declaration') {
        setLoadingDeclarations(true);
        const declarationsResponse = await fetchEquipmentDeclarations(controller, entid);

        if (isZarphSuccessResponse(declarationsResponse)) {
          setDeclarations(declarationsResponse.data?.declarations || []);
        } else {
          handleApiError(declarationsResponse, lang.ALERT_ERROR_FETCHING_DATA);
        }
        setLoadingDeclarations(false);
      }
    } catch (err) {
      handleApiError(err, lang.ALERT_ERROR_FETCHING_DATA);
      setLoadingReasons(false);
      setLoadingDeclarations(false);
    }
  };

  const changePage = (page: number) => {
    const newOffset = (page - 1) * limit;
    setCurrentPage(page);
    setOffset(newOffset);
  };

  const resultsSelect = (newValue: number) => {
    const newLimit = Number(newValue);
    setLimit(newLimit);
    setCurrentPage(1);
    setOffset(0);
  };

  const buttonClicked = (type: string) => {
    setModalData({
      code: 0,
      language: 0,
      text: '',
      type: type
    });
    setEditMode(false);
    setOpenModal(true);
  };

  const rowClicked = (code: number, language: number, text: string, type: string) => {
    setModalData({
      code: code,
      language: language,
      text: text,
      type: type
    });
    setEditMode(true);
    setOpenModal(true);
  };

  const handlecloseModal = () => {
    setOpenModal(false);
    setEditMode(false);
    setModalData({ code: 0, language: 0, text: '', type: '' });
  };

  const handleReload = (type: 'reason' | 'declaration') => {
    setTimeout(() => {
      setOpenModal(false);
      loadLists(type);
    }, 300);
  };

  const getButtonText = (type: string) => {
    return type === 'reason' ? lang.EQUIPMENT_ADD_NEW_REASON : lang.EQUIPMENT_ADD_NEW_DECLARATION;
  };

  const getModalTitle = () => {
    if (editMode) {
      return modalData.type === 'reason' ? lang.EQUIPMENT_UPDATE_REASON : lang.EQUIPMENT_UPDATE_DECLARATION;
    }
    return modalData.type === 'reason' ? lang.EQUIPMENT_ADD_NEW_REASON : lang.EQUIPMENT_ADD_NEW_DECLARATION;
  };

  return (
    <div className="reasons-declarations">
      <Header pageTitle={lang.EQUIPMENT_REASONS_DECLARATIONS} />
      <Group>
        <FilterBar onSearch={loadLists} onDataChange={handleFilterBarOnDataChange} fieldsUsage={{ entity: true }} />
        <Button
          radius="md"
          leftSection={<IconPlus stroke={2} />}
          onClick={() => buttonClicked('reason')}
          className="reason-button"
        >
          {getButtonText('reason')}
        </Button>
        <Button
          radius="md"
          leftSection={<IconPlus stroke={2} />}
          onClick={() => buttonClicked('declaration')}
          className="declaration-button"
        >
          {getButtonText('declaration')}
        </Button>
      </Group>

      {openModal && (
        <EquipmentReasonDeclaration
          show={openModal}
          onClose={handlecloseModal}
          title={getModalTitle()}
          codeReasonDeclaration={modalData.code}
          language={modalData.language}
          text={modalData.text}
          isEditing={editMode}
          type={modalData.type === 'reason' ? 'reason' : 'declaration'}
          onReload={() => handleReload(modalData.type === 'reason' ? 'reason' : 'declaration')}
        />
      )}

      <Group className="tables">
        {/* Reasons Table */}
        <Group className="reason">
          <Table.ScrollContainer minWidth={500} ref={tableRef}>
            <Table striped highlightOnHover verticalSpacing="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{lang.GLOBAL_CODE}</Table.Th>
                  <Table.Th>{lang.GLOBAL_LANGUAGES}</Table.Th>
                  <Table.Th>{lang.EQUIPMENT_DESCRIPTION}</Table.Th>
                  <Table.Th></Table.Th>
                </Table.Tr>
              </Table.Thead>

              <Table.Tbody>
                {loadingReasons ? (
                  <Table.Tr>
                    <Table.Td colSpan={4}>
                      <Loader color="#016273" />
                    </Table.Td>
                  </Table.Tr>
                ) : !reasons ? (
                  <Table.Tr>
                    <Table.Td colSpan={4} style={{ textAlign: 'center' }}>
                      {lang.GLOBAL_NO_RESULTS}
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  reasons?.map((reason, index) => (
                    <Table.Tr key={index} style={{ cursor: 'pointer' }}>
                      <Table.Td onClick={() => rowClicked(reason.code, reason.langid, reason.reason, 'reason')}>
                        {reason.code}
                      </Table.Td>
                      <Table.Td onClick={() => rowClicked(reason.code, reason.langid, reason.reason, 'reason')}>
                        {getLanguageName(reason.langid)}
                      </Table.Td>
                      <Table.Td onClick={() => rowClicked(reason.code, reason.langid, reason.reason, 'reason')}>
                        {reason.reason}
                      </Table.Td>
                      <Table.Td>
                        <DeleteEquipmentReasonDeclaration
                          language={reason.langid}
                          codeReasonDeclaration={reason.code}
                          text={reason.reason}
                          onReload={() => handleReload('reason')}
                          type="reason"
                        />
                      </Table.Td>
                    </Table.Tr>
                  ))
                )}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>

          <Group>
            <PaginationComponent
              total={reasons ? Math.ceil(reasons.length / limit) : 0}
              itemsPerPage={limit}
              dynamicLimit={limit}
              currentPage={currentPage}
              onPageChange={changePage}
              onResultsChange={resultsSelect}
            />
          </Group>
        </Group>

        {/* Declarations Table */}
        <Group className="declaration">
          <Table.ScrollContainer minWidth={500} ref={tableRef}>
            <Table striped highlightOnHover verticalSpacing="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{lang.GLOBAL_CODE}</Table.Th>
                  <Table.Th>{lang.GLOBAL_LANGUAGES}</Table.Th>
                  <Table.Th>{lang.EQUIPMENT_DESCRIPTION}</Table.Th>
                  <Table.Th></Table.Th>
                </Table.Tr>
              </Table.Thead>

              <Table.Tbody>
                {loadingDeclarations ? (
                  <Table.Tr>
                    <Table.Td colSpan={4}>
                      <Loader color="#016273" />
                    </Table.Td>
                  </Table.Tr>
                ) : !declarations ? (
                  <Table.Tr>
                    <Table.Td colSpan={4} style={{ textAlign: 'center' }}>
                      {lang.GLOBAL_NO_RESULTS}
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  declarations?.map((declaration, index) => (
                    <Table.Tr key={index} style={{ cursor: 'pointer' }}>
                      <Table.Td
                        onClick={() =>
                          rowClicked(declaration.code, declaration.langid, declaration.declaration, 'declaration')
                        }
                      >
                        {declaration.code}
                      </Table.Td>
                      <Table.Td
                        onClick={() =>
                          rowClicked(declaration.code, declaration.langid, declaration.declaration, 'declaration')
                        }
                      >
                        {getLanguageName(declaration.langid)}
                      </Table.Td>
                      <Table.Td
                        onClick={() =>
                          rowClicked(declaration.code, declaration.langid, declaration.declaration, 'declaration')
                        }
                      >
                        {declaration.declaration}
                      </Table.Td>
                      <Table.Td>
                        <DeleteEquipmentReasonDeclaration
                          language={declaration.langid}
                          codeReasonDeclaration={declaration.code}
                          text={declaration.declaration}
                          onReload={() => handleReload('declaration')}
                          type="declaration"
                        />
                      </Table.Td>
                    </Table.Tr>
                  ))
                )}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>

          <Group>
            <PaginationComponent
              total={declarations ? Math.ceil(declarations.length / limit) : 0 || totalRows}
              itemsPerPage={limit}
              dynamicLimit={limit}
              currentPage={currentPage}
              onPageChange={changePage}
              onResultsChange={resultsSelect}
              offset={offset}
            />
          </Group>
        </Group>
      </Group>
    </div>
  );
};

export default ReasonsDeclarations;
